<template>
  <div class="slot-inspector-form">
    <p class="slot-inspector-form__intro">{{ intro }}</p>

    <div class="slot-inspector-form__dates">
      <sp-date-field
        ref="startDateField"
        v-preserve-properties:value="startDate"
        class="slot-inspector-form__start-date"
        :input-test-id="TestId.slotInspector.startDate"
        :label="t('start_date_label')"
        allow-input
        :disabled="loading"
        @change="handleStartDateChange"
      ></sp-date-field>
      <sp-text-field
        v-preserve-properties:value="endDateText"
        class="slot-inspector-form__end-date"
        :label="t('end_date_label')"
        :hint="t('end_date_hint')"
        persistent-hint
        disabled
      ></sp-text-field>
    </div>

    <sp-button
      ref="submitButton"
      class="slot-inspector-form__submit"
      :data-testid="TestId.slotInspector.submit"
      :loading="loading"
      :disabled="submitDisabled"
      @click="submit"
    >
      {{ t("submit_label") }}
    </sp-button>
  </div>
</template>

<script setup>
import { computed, nextTick, useTemplateRef, watch } from "vue";
import { TestId } from "#/test-ids";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { t } from "#ce/apps/SpAdmin/SpSlotInspectorApp/i18n";
import { STATUS, useSlotInspectorStore } from "#ce/apps/SpAdmin/SpSlotInspectorApp/store/slot-inspector";
import { formatDisplayDate } from "#ce/apps/SpAdmin/SpSlotInspectorApp/dates";

const props = defineProps({
  recordType: {
    type: String,
    default: "physician",
  },
});

const { status, startDate, endDate, loading, setStartDate, submit } = useSlotInspectorStore();

const startDateField = useTemplateRef("startDateField");
const submitButton = useTemplateRef("submitButton");

const intro = computed(() => t(props.recordType === "location" ? "intro_location" : "intro_physician"));
const endDateText = computed(() => formatDisplayDate(endDate.value));
const submitDisabled = computed(() => loading.value || !startDate.value);

function handleStartDateChange({ detail }) {
  setStartDate(detail[0]);
}

// Returns focus to the button after an error or an empty preview, both of which keep the user on the form. Disabling
// the button for the load dropped its focus, and the status region announces the outcome. The button re-enables in
// its own render, a tick after this form's.
async function refocusSubmitAfterPreview(current) {
  if (current !== STATUS.ERROR && current !== STATUS.EMPTY) {
    return;
  }

  await nextTick();
  submitButton.value?.focus();
}

function focusStartDate() {
  startDateField.value?.focus();
}

watch(status, refocusSubmitAfterPreview, { flush: "post" });

defineExpose({ focusStartDate });
</script>

<style scoped lang="scss">
.slot-inspector-form {
  display: grid;
  gap: var(--sp-ref-scale-24);

  &__intro {
    margin: 0;
    font-size: var(--sp-sys-typescale-small-font-size);
    color: var(--sp-sys-color-on-surface);
  }

  &__dates {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--sp-ref-scale-16);
  }

  &__submit {
    justify-self: start;
  }
}
</style>
